import cuid from "cuid";
import { Reducer } from "redux";

import { PubsubWebSocket } from "./websocket";

import { BaseLogger } from "base_logger";
import {
  AckStateType,
  ActionDirectlyFromClientType,
  EnhancedReducerActionType,
  MessageFromClientToServerType,
  OriginalReducerActionType,
  PublishableActionType,
} from "shared/types";

export function getPublishableAction(
  action: ActionDirectlyFromClientType | PublishableActionType,
  publisherId: string,
): PublishableActionType {
  return {
    ...action,
    meta: {
      psid: cuid(),
      // This is the time the action was published by the client.
      // Since it is only used for calculating latency, it is not
      // critical that it be accurate.
      clientTimestamp: Date.now(),
      publisherId,
    },
  };
}

export type SliceOptionsType<StateType> = {
  channel?: string;
  reducer: Reducer<StateType, OriginalReducerActionType>;
  requestsSync?: boolean;
  publishActions?: boolean;
  flightAuthToken?: string;
  userId: string;
  origin: "classroom" | "monitoring";
  serverUrl: string;
  monitoringServerUrl: string;
  path?: string;
};

// Base class for ReduxPubsubSlice and AckReduxPubsubSlice
// This class handles the socket connection, message buffering
// and creates the enhanced slice reducers that will publish the actions to the server.
export abstract class BaseReduxPubsubSlice<StateType> extends BaseLogger {
  channel?: string;

  requestsSync: boolean;

  publishActions?: boolean;

  flightAuthToken?: string;

  userId: string;

  origin: "classroom" | "monitoring";

  path?: string;

  serverUrl: string;

  monitoringServerUrl: string;

  _reducer: Reducer<StateType, OriginalReducerActionType>;

  sendBuffer: MessageFromClientToServerType[] = [];

  socket?: PubsubWebSocket;

  constructor(options: SliceOptionsType<StateType>) {
    super();
    this.channel = options.channel;
    this.requestsSync = typeof options.requestsSync === "boolean" ? options.requestsSync : true;
    this.publishActions = options.publishActions;
    this.flightAuthToken = options.flightAuthToken;
    this.userId = options.userId;
    this.origin = options.origin;
    this.serverUrl = options.serverUrl;
    this.monitoringServerUrl = options.monitoringServerUrl;
    this.path = options.path;
    this._reducer = options.reducer;
    this.validate();
  }

  validate() {
    if (typeof this._reducer !== "function") {
      throw new Error("reducer must be a function");
    }
  }

  socketSend(msg: MessageFromClientToServerType) {
    if (this.socket?.isOpen()) {
      this.socket.send(JSON.stringify(msg));
    } else {
      this.sendBuffer.push(msg);
    }
  }

  private handleOpenSocket() {
    // Duplicate the buffer before emptying it. This way, if the socket
    // disconnects midway through sending the buffered messages, we won't end
    // up losing them.
    const buffered = [...this.sendBuffer];
    // eslint-disable-next-line no-param-reassign
    this.sendBuffer = [];
    buffered.forEach((msg) => this.socketSend(msg));
    // TODO: treat first sync request and subsequent sync requests differently.
    // For subsequent sync requests, maybe we can only request the diff.
    if (this.requestsSync) {
      this.socketSend({ type: "request-sync" });
    }
  }

  setUpSocket() {
    if (!this.serverUrl) {
      (window.flightLogger || console).warn("[setUpSocket] serverUrl is not defined");
      return;
    }
    if (!this.channel || !this.flightAuthToken || !this.userId) return;
    this.socket = new PubsubWebSocket({
      channel: this.channel,
      flightAuthToken: this.flightAuthToken,
      userId: this.userId,
      origin: this.origin,
      serverUrl: this.serverUrl,
      monitoringServerUrl: this.monitoringServerUrl,
      path: this.path,
    });
    if (this.socket?.isOpen()) {
      this.handleOpenSocket();
    }
    this.socket.addEventListener("open", () => this.handleOpenSocket());
  }

  reduce(state: StateType | undefined, action: OriginalReducerActionType) {
    return this._reducer(state, action);
  }

  // Enhance the slice reducer, which will be called whenever an action is dispatched.
  // - For publishable slices, the reducer will also publish the action to the server.
  // - For syncable slices, the reducer will also reset the local state when receiving a sync message.
  // - For ackable slices, the reducer will
  //   - apply the action to the local state
  //   - add the action to the pendingActions queue
  //   - publish the action to the server
  //   - wait for the server to acknowledge the action
  //   - if the server rejects the action, remove it from the pendingActions queue and undo it on the local state
  //   - if the server accepts the action, remove it from the pendingActions queue and apply it to the server state
  abstract getReducer(
    requestsSync: boolean,
    publishActions: boolean,
  ):
    | Reducer<StateType, EnhancedReducerActionType>
    | Reducer<AckStateType<StateType>, EnhancedReducerActionType>;
}
